<template>
  <div class="landing-page min-h-screen bg-slate-950 text-white overflow-hidden">
    <!-- Hero Section -->
    <header class="relative pt-20 pb-32 px-6">
      <div class="absolute inset-0 z-0">
        <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-600/20 rounded-full blur-[120px]"></div>
        <div class="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px]"></div>
      </div>

      <div class="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-12 items-center">
        <div class="space-y-8 animate-fade-in">
          <div class="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
            <span class="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
            <span class="text-sm font-medium tracking-wide text-orange-200">Alohin 3.0 está aqui</span>
          </div>

          <h1 class="text-5xl lg:text-7xl font-black leading-tight bg-gradient-to-r from-white via-white to-orange-400 bg-clip-text text-transparent">
            Posicione sua imobiliária <br /> no topo do mercado
          </h1>

          <p class="text-xl text-slate-400 leading-relaxed max-w-xl">
            Mais visibilidade, mais autoridade e mais vendas. O CRM imobiliário queridinho dos corretores ficou ainda melhor. Quem se conecta primeiro, vende melhor.
          </p>

          <div class="flex flex-col sm:flex-row gap-4 pt-4">
            <router-link to="/cadastro" class="btn-primary">
              Começar Agora
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </router-link>
            <a href="#beneficios" class="btn-secondary">Peça uma Demonstração</a>
          </div>
        </div>

        <div class="relative animate-float lg:block hidden">
          <div class="absolute-center w-[120%] h-[120%] bg-orange-500/10 rounded-full blur-[80px]"></div>
          <img 
            src="../assets/images/hero-illustration.png" 
            alt="Aloh.in Hero Illustration" 
            class="rounded-[3rem] shadow-2xl border border-white/10 relative z-10"
          />
        </div>
      </div>
    </header>

    <!-- Benefits Section -->
    <section id="beneficios" class="py-24 px-6 bg-slate-900/50 backdrop-blur-sm border-y border-white/5">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-20">
          <h2 class="text-4xl font-bold mb-4">Soluções para desafios bem concretos</h2>
          <p class="text-slate-400">A Aloh.in é sua parceira completa no mundo digital imobiliário.</p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="(item, i) in benefits" :key="i" class="feature-card">
            <div class="icon-box" :style="{ background: item.color }">
              <span class="text-2xl">{{ item.icon }}</span>
            </div>
            <h3 class="text-xl font-bold mb-2">{{ item.title }}</h3>
            <p class="text-slate-400 text-sm leading-relaxed">{{ item.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="py-32 px-6 text-center relative">
      <div class="absolute inset-0 bg-gradient-to-b from-transparent via-orange-600/5 to-transparent"></div>
      <div class="max-w-3xl mx-auto relative z-10">
        <h2 class="text-5xl font-bold mb-8">O CRM imobiliário queridinho dos corretores</h2>
        <router-link to="/cadastro" class="btn-primary-lg">
          Começar Teste Grátis
        </router-link>
      </div>
    </section>
  </div>
</template>

<script setup>
const benefits = [
  {
    title: 'Gestão de Imóveis',
    icon: '🏠',
    color: 'linear-gradient(135deg, #2563eb, #3b82f6)',
    text: 'Cadastre imóveis com eficiência e simplicidade total.'
  },
  {
    title: 'Gestão de Usuários',
    icon: '👥',
    color: 'linear-gradient(135deg, #f97316, #fb923c)',
    text: 'Controle fácil de todos os corretores e usuários da plataforma.'
  },
  {
    title: 'Gestão de Atendimentos',
    icon: '📞',
    color: 'linear-gradient(135deg, #059669, #10b981)',
    text: 'Controle contatos, atendimentos e tarefas em um só lugar.'
  },
  {
    title: 'Page Builder',
    icon: '🎨',
    color: 'linear-gradient(135deg, #8b5cf6, #a78bfa)',
    text: 'Editor visual de layout "arrastar e soltar" para o seu site.'
  }
];

const stats = [
  { value: 'Mais leads', label: 'Qualificados' },
  { value: 'Mais vendas', label: 'Conversões' },
  { value: 'Mais autoridade', label: 'No Mercado' },
  { value: 'Mais tempo', label: 'Otimização' }
];
</script>

<style lang="scss" scoped>
.landing-page {
  scroll-behavior: smooth;
}

.animate-fade-in {
  animation: fadeIn 1s ease-out forwards;
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

.btn-primary {
  @apply inline-flex items-center gap-2 px-8 py-4 rounded-full bg-orange-600 font-bold text-white shadow-xl shadow-orange-900/20 hover:bg-orange-500 hover:-translate-y-1 active:translate-y-0 transition-all;
}

.btn-primary-lg {
  @apply inline-block px-12 py-5 rounded-full bg-orange-600 font-black text-xl text-white shadow-2xl shadow-orange-900/40 hover:bg-orange-500 hover:-translate-y-1 transition-all;
}

.btn-secondary {
  @apply px-8 py-4 rounded-full bg-white/5 border border-white/10 font-bold text-white backdrop-blur-md hover:bg-white/10 transition-all;
}

.feature-card {
  @apply p-8 rounded-3xl bg-slate-900 border border-white/5 hover:border-orange-500/30 hover:bg-slate-800/80 transition-all duration-300;
  
  .icon-box {
    @apply w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-lg;
  }
}

.absolute-center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  position: absolute;
}
</style>
